import { User } from '../models/User';

/**
 * Gets display name for a user
 */
export const getDisplayName = (user: User): string => {
  return user.fullName || 'Unknown User';
};

/**
 * Gets initials from a name for privacy display
 */
export const getInitials = (name: string): string => {
  if (!name) return '';
  
  const nameParts = name.split(' ');
  if (nameParts.length === 1) {
    return `${nameParts[0][0]}.`;
  }
  return nameParts.map(part => `${part[0]}.`).join(' ');
};

/**
 * Gets display name with privacy options
 */
export const getDisplayNameWithPrivacy = (
  name: string, 
  showFullName: boolean, 
  isMatching: boolean = false
): string => {
  // Always show full name if there is a match, regardless of privacy settings
  if (isMatching) return name;
  
  if (!name) return '';
  if (showFullName) return name;
  
  return getInitials(name);
};

/**
 * Checks if user has admin privileges
 */
export const hasAdminPrivileges = (user: User): boolean => {
  return Boolean(user.isAdmin) || Boolean(user.isSuperAdmin);
};

/**
 * Checks if user has super admin privileges
 */
export const hasSuperAdminPrivileges = (user: User): boolean => {
  return Boolean(user.isSuperAdmin);
};

/**
 * Checks if user can perform action (not suspended and authenticated)
 */
export const canUserPerformAction = (user: User | null): boolean => {
  return Boolean(user && !user.suspended);
};

// Note: getUserTypeBadgeClass function removed as userType is no longer used

/**
 * Gets user role display text
 */
export const getUserRoleText = (user: User): string => {
  if (user.isSuperAdmin) return 'Super Admin';
  if (user.isAdmin) return 'Admin';
  return 'User';
};

/**
 * Formats user contact information
 */
export const formatUserContact = (user: User): {
  email?: string;
  phone?: string;
  country?: string;
} => {
  return {
    email: user.email,
    phone: user.phoneNumber,
    country: user.country
  };
};

/**
 * Checks if two users are the same
 */
export const isSameUser = (user1: User | null, user2: User | null): boolean => {
  if (!user1 || !user2) return false;
  return user1.id === user2.id;
};

/**
 * Validates user permissions for content creation/editing
 */
export const canUserEditContent = (user: User | null, authorName?: string): boolean => {
  if (!user || user.suspended) return false;
  
  // Admins can edit any content
  if (hasAdminPrivileges(user)) return true;
  
  // Users can edit their own content
  if (authorName) {
    const userDisplayName = getDisplayName(user);
    return userDisplayName === authorName;
  }
  
  return false;
};

/**
 * Validates user permissions for deletion
 */
export const canUserDelete = (user: User | null, itemOwner?: string): boolean => {
  if (!user || user.suspended) return false;
  
  // Super admins can delete anything
  if (hasSuperAdminPrivileges(user)) return true;
  
  // Regular admins can delete most things
  if (user.isAdmin) return true;
  
  // Users can delete their own items
  if (itemOwner) {
    const userDisplayName = getDisplayName(user);
    return userDisplayName === itemOwner;
  }
  
  return false;
};

/**
 * Gets user avatar initials for display
 */
export const getAvatarInitials = (user: User): string => {
  const displayName = getDisplayName(user);
  const nameParts = displayName.split(' ');
  
  if (nameParts.length >= 2) {
    return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
  } else if (nameParts.length === 1) {
    return nameParts[0].slice(0, 2).toUpperCase();
  }
  
  return 'U';
}; 